/**
 * Who sees a layer, in a chip — `docs/property-maps-v2.md` §5.5.
 *
 * **The words come from `audienceLabel` and nowhere else** (§12 rule 2): `All
 * crews`, `Snow`, `Snow · Hardscape`, `Snow +2`, `Only me`. Every surface that
 * lists a layer draws this one component, which is what stops the panel row,
 * the switcher card and the phone sheet disagreeing about who a layer is for.
 *
 * Three shapes, because the three answers are different in kind rather than in
 * degree: the default wears a people glyph, a division list wears a dot per
 * division in that division's own colour, and `Only me` wears a lock and is
 * muted — it is the one that never reaches a crew.
 */
import { audienceLabel, type Audience } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { IconLock, IconTeam } from '../../frame/icons.js'

export interface Division { id: string; name: string; colour: string }

export function AudienceChip({
  audience, divisionIds, divisions, className,
}: {
  audience: Audience
  divisionIds: string[]
  divisions: Division[]
  className?: string
}) {
  const byId = new Map(divisions.map((one) => [one.id, one.name]))
  const label = audienceLabel({ audience, divisionIds }, byId)

  if (audience !== 'divisions') {
    return (
      <span className={cn('inline-flex items-center gap-1 text-text-muted', className)}>
        {audience === 'me' ? <IconLock size={11} /> : <IconTeam size={11} />}
        {label}
      </span>
    )
  }

  /* A dot per division, in the division's own colour — at most two, matching
     `audienceLabel`'s two names, so the dots and the words agree. */
  const dots = divisionIds
    .map((id) => divisions.find((one) => one.id === id))
    .filter((one): one is Division => Boolean(one))
    .slice(0, 2)

  return (
    <span className={cn('inline-flex items-center gap-1.5 text-text-muted', className)}>
      {dots.map((division) => (
        <span
          key={division.id}
          aria-hidden
          className="h-[7px] w-[7px] shrink-0 rounded-chip"
          style={{ background: division.colour }}
        />
      ))}
      {label}
    </span>
  )
}
